import React, {
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react'
import { useLocalStorage } from '../hooks/useLocalStorage'
// TODO: 04/21/24 build a retreival of onHandQty maintained by aws
//   → partially addressed: see data/stockOutages.js STOCK_OUTAGES_URL
import { products } from '../data'
import {
    trackAddToCart,
    trackRemoveFromCart,
} from '../utilities/trackingEvents'
import detectInitialCurrencyIsCAD from '../utilities/detectInitialCurrency'
import {
    useStockOutages,
    isOutOfStock,
    marketFromIsCAD,
} from '../data/stockOutages'

const ShoppingCartContext = createContext({})

export function useShoppingCart() {
    return useContext(ShoppingCartContext)
}

export function ShoppingCartProvider({ children }) {
    const [showCart, setShowCart] = useState(false)

    const [cart, setCart] = useLocalStorage('shopping-cart', [])

    // Initial currency: USD unless BOTH browser locale and timezone point
    // to Canada (see utilities/detectInitialCurrency.js). Once the user
    // has chosen, localStorage wins and detection is never consulted again.
    //
    // NOTE: if your deployed context still reads
    //     useLocalStorage('isCAD', false)
    // keep that line instead — the outage logic below does not depend on
    // which seed you use.
    const [isCAD, setIsCADCurrency] = useLocalStorage(
        'isCAD',
        detectInitialCurrencyIsCAD(),
    )

    const data = products[isCAD ? 1 : 0] // USD array or CAD array
    const currency = isCAD ? 'CAD' : 'USD'
    const market = marketFromIsCAD(isCAD) // 'US' | 'CA'
    const cartQty = cart.reduce((quantity, item) => item.quantity + quantity, 0)

    // ─── Stock outages ────────────────────────────────────────────────
    // Map of productIDNum → { markets, hide, note }. Static by default;
    // remote-controlled if STOCK_OUTAGES_URL is set in stockOutages.js.
    const outages = useStockOutages()

    // Bound helper so components don't need to know about markets.
    const isItemOutOfStock = (productIDNum) =>
        isOutOfStock(outages, productIDNum, market)

    // Names of items removed from the cart by the purge below, so the cart
    // page can tell the customer what happened instead of silently
    // shrinking their order. Session state only — cleared on dismiss.
    const [purgedItems, setPurgedItems] = useState([])
    const clearPurgedItems = () => setPurgedItems([])

    // Purge: a cart saved in localStorage before a stockout — or one
    // carried over from the calculator — can contain items that are now
    // out of stock in this market. Remove them whenever the cart, the
    // market, or the outage list changes. Re-runs once after its own
    // setCart and finds nothing, so it cannot loop.
    useEffect(() => {
        const blocked = cart.filter((item) =>
            isOutOfStock(outages, item.productIDNum, market),
        )
        if (blocked.length === 0) return

        const names = blocked.map((b) => {
            const p = data.find((d) => d.productIDNum === b.productIDNum)
            return p ? p.descriptionTitle : `Item ${b.productIDNum}`
        })
        setPurgedItems((prev) => Array.from(new Set([...prev, ...names])))
        setCart((currItems) =>
            currItems.filter(
                (item) => !isOutOfStock(outages, item.productIDNum, market),
            ),
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart, outages, market])

    // ─── Helpers ──────────────────────────────────────────────────────
    function findProduct(productIDNum) {
        return data.find((p) => p.productIDNum === productIDNum)
    }

    function getItemQty(productIDNum) {
        return (
            cart.find((item) => item.productIDNum === productIDNum)?.quantity ||
            0
        )
    }

    // ─── Mutations ────────────────────────────────────────────────────
    function incrItemQty(productIDNum) {
        // Out-of-stock items cannot be added in this market, regardless
        // of how the call reaches us (grid, cart page, stale UI).
        if (isItemOutOfStock(productIDNum)) return

        const product = findProduct(productIDNum)
        if (product) trackAddToCart(product, 1, currency)

        setCart((currItems) => {
            const existing = currItems.find(
                (item) => item.productIDNum === productIDNum,
            )
            if (existing == null) {
                return [...currItems, { productIDNum, quantity: 1 }]
            }
            return currItems.map((item) =>
                item.productIDNum === productIDNum
                    ? { ...item, quantity: item.quantity + 1 }
                    : item,
            )
        })
    }

    function decrItemQty(productIDNum) {
        // Defensive: no-op if the item isn't in the cart (prevents stale
        // callers from spamming tracking events / re-renders).
        const existing = cart.find(
            (item) => item.productIDNum === productIDNum,
        )
        if (existing == null) return

        const product = findProduct(productIDNum)
        if (product) trackRemoveFromCart(product, 1, currency)

        setCart((currItems) => {
            const current = currItems.find(
                (item) => item.productIDNum === productIDNum,
            )
            if (current == null) return currItems
            if (current.quantity <= 1) {
                return currItems.filter(
                    (item) => item.productIDNum !== productIDNum,
                )
            }
            return currItems.map((item) =>
                item.productIDNum === productIDNum
                    ? { ...item, quantity: item.quantity - 1 }
                    : item,
            )
        })
    }

    function removeFromCart(productIDNum) {
        const product = findProduct(productIDNum)
        const existing = cart.find(
            (item) => item.productIDNum === productIDNum,
        )
        if (existing == null) return

        if (product) trackRemoveFromCart(product, existing.quantity, currency)

        setCart((currItems) =>
            currItems.filter((item) => item.productIDNum !== productIDNum),
        )
    }

    function setItemQty(productIDNum, qty) {
        if (isItemOutOfStock(productIDNum)) return

        setCart((currItems) => {
            if (
                currItems.find((item) => item.productIDNum === productIDNum) ==
                null
            ) {
                return [...currItems, { productIDNum, quantity: qty }]
            }
            return currItems.map((item) =>
                item.productIDNum === productIDNum
                    ? { ...item, quantity: qty }
                    : item,
            )
        })
    }

    function emptyCart() {
        setCart([])
    }

    return (
        <ShoppingCartContext.Provider
            value={{
                getItemQty,
                setItemQty,
                incrItemQty,
                decrItemQty,
                removeFromCart,
                emptyCart,
                showCart,
                setShowCart,
                cart,
                cartQty,
                isCAD,
                setIsCADCurrency,
                data,
                // stock outages
                outages,
                market,
                isItemOutOfStock,
                purgedItems,
                clearPurgedItems,
            }}
        >
            {children}
        </ShoppingCartContext.Provider>
    )
}
